/**
 * Subscription Tiers Configuration
 * Defines pricing, limits, and features for each subscription tier
 * HoneyDo Platform - Multi-Relationship Collaboration
 */

export const SUBSCRIPTION_TIERS = {
  STARTER: 'Starter',
  PREMIUM: 'Premium',
  ENTERPRISE: 'Enterprise'
};

export const RELATIONSHIP_TYPES = {
  // Household (Free + Premium)
  NUCLEAR_FAMILY: {
    id: 'nuclear_family',
    name: 'Nuclear Family',
    description: 'Parents and children living together',
    icon: '👨‍👩‍👧‍👦',
    category: 'Household',
    tier: 'Free'
  },
  COUPLES_PARTNERS: {
    id: 'couples_partners',
    name: 'Couples & Partners',
    description: 'Two-person households',
    icon: '💑',
    category: 'Household',
    tier: 'Premium'
  },
  ROOMMATES: {
    id: 'roommates',
    name: 'Roommates',
    description: 'Shared living arrangements',
    icon: '🏠',
    category: 'Household',
    tier: 'Premium'
  },

  // Real Estate (Premium)
  TENANT_LANDLORD: {
    id: 'tenant_landlord',
    name: 'Tenant & Landlord',
    description: 'Property management and maintenance',
    icon: '🔑',
    category: 'Real Estate',
    tier: 'Premium'
  },
  VACATION_HOME: {
    id: 'vacation_home',
    name: 'Vacation Home',
    description: 'Seasonal property management',
    icon: '🏖️',
    category: 'Real Estate',
    tier: 'Premium'
  },
  BUILDER_BUYER: {
    id: 'builder_buyer',
    name: 'Builder & Buyer',
    description: 'New construction project tracking',
    icon: '🏗️',
    category: 'Real Estate',
    tier: 'Enterprise'
  },
  RENOVATION_PROJECT: {
    id: 'renovation_project',
    name: 'Renovation Project',
    description: 'Home improvement coordination',
    icon: '🔨',
    category: 'Real Estate',
    tier: 'Enterprise'
  },

  // Community (Premium + Enterprise)
  SPORTS_TEAM: {
    id: 'sports_team',
    name: 'Sports Team',
    description: 'Team activities and scheduling',
    icon: '⚽',
    category: 'Community',
    tier: 'Premium'
  },
  PERFORMANCE_GROUP: {
    id: 'performance_group',
    name: 'Performance Group',
    description: 'Arts, theater, and music groups',
    icon: '🎭',
    category: 'Community',
    tier: 'Enterprise'
  },
  GROUP_TRIP: {
    id: 'group_trip',
    name: 'Group Trip',
    description: 'Travel planning and coordination',
    icon: '✈️',
    category: 'Community',
    tier: 'Premium'
  },
  EVENT_PLANNING: {
    id: 'event_planning',
    name: 'Event Planning',
    description: 'Weddings, parties, and celebrations',
    icon: '🎉',
    category: 'Community',
    tier: 'Enterprise'
  },

  // Professional (Enterprise)
  SMALL_BUSINESS: {
    id: 'small_business',
    name: 'Small Business',
    description: 'Business operations and tasks',
    icon: '💼',
    category: 'Professional',
    tier: 'Enterprise'
  },
  HOA_COMMUNITY: {
    id: 'hoa_community',
    name: 'HOA Community',
    description: 'Homeowners association management',
    icon: '🏘️',
    category: 'Professional',
    tier: 'Enterprise'
  },
  COOPERATIVE: {
    id: 'cooperative',
    name: 'Cooperative',
    description: 'Co-op housing and shared ownership',
    icon: '🤝',
    category: 'Professional',
    tier: 'Enterprise'
  }
};

export const TIER_CONFIG = {
  [SUBSCRIPTION_TIERS.STARTER]: {
    name: 'Starter',
    price: 0.99,
    priceMonthly: '$0.99/month',
    priceYearly: '$0.99/month',
    description: 'Essential household management',
    features: {
      maxMembers: 2,
      maxEvents: 10,
      maxRelationshipTypes: 1,
      allowedRelationshipTypes: ['nuclear_family'],
      weatherIntegration: false,
      seasonalAutomation: false,
      contractorNetwork: false,
      prioritySupport: false,
      analytics: false,
      customBranding: false,
      apiAccess: false
    },
    featureList: [
      'Up to 2 adult users',
      '50 active tasks',
      'Basic task management',
      'Mobile app access',
      'Community support'
    ],
    cta: 'Get Started',
    popular: false
  },

  [SUBSCRIPTION_TIERS.PREMIUM]: {
    name: 'Premium',
    price: 9.99,
    priceMonthly: '$9.99/month',
    priceYearly: '$99/year',
    yearlyDiscount: '17% off',
    description: 'Advanced features for growing teams',
    features: {
      maxMembers: 15,
      maxEvents: 999999, // Unlimited
      maxRelationshipTypes: 5,
      allowedRelationshipTypes: [
        'nuclear_family',
        'couples_partners',
        'roommates',
        'tenant_landlord',
        'vacation_home',
        'sports_team',
        'group_trip'
      ],
      weatherIntegration: true,
      seasonalAutomation: true,
      contractorNetwork: true,
      prioritySupport: false,
      analytics: true,
      customBranding: false,
      apiAccess: false
    },
    featureList: [
      'Up to 15 members',
      'Unlimited events',
      '5 relationship types',
      'Weather-intelligent scheduling',
      'Seasonal automation',
      'Contractor network access',
      'Advanced analytics',
      'Priority email support'
    ],
    cta: 'Upgrade to Premium',
    popular: true
  },

  [SUBSCRIPTION_TIERS.ENTERPRISE]: {
    name: 'Enterprise',
    price: 29.99,
    priceMonthly: '$29.99/month',
    priceYearly: '$299/year',
    yearlyDiscount: '17% off',
    description: 'Everything for large organizations',
    features: {
      maxMembers: 999999, // Unlimited
      maxEvents: 999999, // Unlimited
      maxRelationshipTypes: 999999, // All types
      allowedRelationshipTypes: Object.keys(RELATIONSHIP_TYPES).map(key =>
        RELATIONSHIP_TYPES[key].id
      ),
      weatherIntegration: true,
      seasonalAutomation: true,
      contractorNetwork: true,
      prioritySupport: true,
      analytics: true,
      customBranding: true,
      apiAccess: true
    },
    featureList: [
      'Unlimited members',
      'Unlimited events',
      'All 14 relationship types',
      'Weather intelligence',
      'Seasonal automation',
      'Contractor network',
      'Advanced analytics',
      'Custom branding',
      'API access',
      'Priority phone support',
      'Dedicated account manager'
    ],
    cta: 'Upgrade to Enterprise',
    popular: false
  }
};

/**
 * Get configuration for a specific tier
 */
export const getTierConfig = (tierName) => {
  return TIER_CONFIG[tierName] || TIER_CONFIG[SUBSCRIPTION_TIERS.STARTER];
};

/**
 * Get all relationship types available for a tier
 */
export const getRelationshipTypesForTier = (tierName) => {
  const config = getTierConfig(tierName);
  return Object.values(RELATIONSHIP_TYPES).filter(type =>
    config.features.allowedRelationshipTypes.includes(type.id)
  );
};

/**
 * Get relationship types grouped by category
 */
export const getRelationshipTypesByCategory = () => {
  const categories = {};
  Object.values(RELATIONSHIP_TYPES).forEach(type => {
    if (!categories[type.category]) {
      categories[type.category] = [];
    }
    categories[type.category].push(type);
  });
  return categories;
};

/**
 * Check if a relationship type is available in a tier
 */
export const isRelationshipTypeAvailable = (relationshipTypeId, tierName) => {
  const config = getTierConfig(tierName);
  return config.features.allowedRelationshipTypes.includes(relationshipTypeId);
};

/**
 * Get the minimum tier required for a relationship type
 */
export const getRequiredTierForRelationshipType = (relationshipTypeId) => {
  const type = Object.values(RELATIONSHIP_TYPES).find(t => t.id === relationshipTypeId);
  return type ? type.tier : SUBSCRIPTION_TIERS.ENTERPRISE;
};

export default {
  SUBSCRIPTION_TIERS,
  RELATIONSHIP_TYPES,
  TIER_CONFIG,
  getTierConfig,
  getRelationshipTypesForTier,
  getRelationshipTypesByCategory,
  isRelationshipTypeAvailable,
  getRequiredTierForRelationshipType
};
